import { useCallback, useEffect, useState } from 'react'
import OverlayIpcRenderer from '@renderer/ipc/OverlayIpc.renderer'

/** Idle, or the outcome of the last copy — never both, so the button can't read as half-succeeded. */
export type CopyBriefStatus = 'idle' | 'copied' | 'error'

/**
 * Copy a brief to the clipboard, reporting the outcome for 2s the way the app's
 * other copy buttons do. Not `useCopySummary` — that one runs the text through
 * `cleanAudioNoteSummary`, which strips audio-note scaffolding a brief doesn't
 * have and shouldn't be measured against.
 */
export function useCopyBrief(brief: string | undefined) {
  const [status, setStatus] = useState<CopyBriefStatus>('idle')
  const text = brief?.trim() ?? ''

  const handleCopy = useCallback(async () => {
    if (!text) {
      return
    }
    // Idle first so a retry starts clean, and so a second failure restarts the
    // reset timer below rather than inheriting the first one's remaining time.
    setStatus('idle')
    try {
      await OverlayIpcRenderer.writeTextToClipboard(text)
      setStatus('copied')
    } catch (error) {
      console.error('Failed to copy brief:', error)
      setStatus('error')
    }
  }, [text])

  useEffect(() => {
    if (status === 'idle') {
      return
    }
    const timeout = setTimeout(() => setStatus('idle'), 2000)
    return () => clearTimeout(timeout)
  }, [status])

  return { status, handleCopy, hasBrief: !!text }
}
